"use client";

import Link from "@/components/ui/Link";
import { useEffect, useState } from "react";
import { sanitizeHTML } from "@/lib/utils";

interface TableOfContentsData {
}

export const fields = [
] as const;

interface TableOfContentsProps {
  data: TableOfContentsData;
}

type SectionRef = {
  id: string;
  name: string;
};

export default function TableOfContents({
  data
}: TableOfContentsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [sections, setSections] = useState<SectionRef[]>([]);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector("header");
      if (header) {
        setHeaderHeight(header.offsetHeight);
      }
    };

    const setScrollMargin = () => {
      const anchors = document.querySelectorAll('[id]');
      anchors.forEach(anchor => {
        (anchor as HTMLElement).style.scrollMarginTop = '140px';
      });
    };

    const collectSections = () => {
      const nodeList = Array.from(document.querySelectorAll('section[id]')) as HTMLElement[];
      const mapped = nodeList
        .map((el) => {
          const id = el.getAttribute('id') || "";
          const name = el.getAttribute('data-name') || "";
          return { id, name };
        })
        .filter(s => s.id && s.name);
      setSections((prev) => {
        const prevSignature = prev.map((s) => `${s.id}:${s.name}`).join('|');
        const nextSignature = mapped.map((s) => `${s.id}:${s.name}`).join('|');
        return prevSignature === nextSignature ? prev : mapped;
      });
    };

    updateHeaderHeight();
    setScrollMargin();
    collectSections();
    const rafId = window.requestAnimationFrame(collectSections);
    const timeout200 = window.setTimeout(collectSections, 200);
    const timeout800 = window.setTimeout(collectSections, 800);

    const handleResize = () => {
      updateHeaderHeight();
      setScrollMargin();
      collectSections();
    };

    const handleLoad = () => {
      setScrollMargin();
      collectSections();
    };

    const observer = new MutationObserver(() => {
      setScrollMargin();
      collectSections();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'data-name']
    });

    window.addEventListener("resize", handleResize);
    window.addEventListener("load", handleLoad);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("load", handleLoad);
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeout200);
      window.clearTimeout(timeout800);
      observer.disconnect();
    };
  }, []);

  return (
		<section
			id='page-section-navigation'
			className='w-full sticky top-[73px] md:top-[94px] z-[99]'
		>
			<div id='anchors' className='relative w-full'>
				<div className='w-full'>
					<button
						onClick={() => setIsOpen(!isOpen)}
						className='w-full bg-black cursor-pointer'
					>
						<div className='px-5 md:px-0 py-5 w-full'>
							<div className='flex flex-col md:flex-row md:items-center md:justify-between w-full container mx-auto'>
								<span className='text-white text-lg font-medium text-left'>
									JUMP TO SECTION:
								</span>
								<div className='md:flex items-center'>
									<div className='text-white md:hidden text-lg w-fit relative pr-6'>
										{selectedSection || 'Select Section'}
										<span className='absolute right-0 top-1/2 -mt-1 border-[6px] border-transparent border-t-white'></span>
									</div>
									<div className='hidden md:flex items-center text-white text-sm font-medium'>
										{isOpen ? 'Hide sections' : 'Show sections'}
										<span
											className={`ml-2 transition-transform duration-200 ${
												isOpen ? 'rotate-180' : ''
											}`}
										>
											<svg
												width='12'
												height='8'
												viewBox='0 0 12 8'
												fill='none'
												xmlns='http://www.w3.org/2000/svg'
											>
												<path
													d='M1 1L6 6L11 1'
													stroke='currentColor'
													strokeWidth='2'
													strokeLinecap='round'
												/>
											</svg>
										</span>
									</div>
								</div>
							</div>
						</div>
					</button>
					<div
						className={`${
							isOpen ? 'block' : 'hidden'
						} left-0 top-full bg-black md:bg-white px-6 pt-6 pb-12 md:p-0 min-w-[285px] w-full z-10 transition-all duration-300 max-h-[calc(100vh-140px)] md:max-h-none overflow-y-auto md:overflow-visible`}
					>
						<div className='container mx-auto'>
							<ul className='space-y-2 md:space-y-0 md:grid md:grid-rows-5 md:grid-flow-col md:auto-cols-max xl:grid-rows-4 md:gap-x-8 md:gap-y-4 md:border-b md:border-black/20 md:py-6 md:justify-between px-0'>
								{sections.map((link, index) => (
									<li key={index} className='w-full block pb-0'>
										<Link
											href={`#${link.id}`}
											onClick={() => {
												setSelectedSection(link.name)
												setIsOpen(false)
												const element = document.getElementById(link.id)
												if (element) {
													element.scrollIntoView({ behavior: 'smooth' })
												}
											}}
											className='text-white md:text-black hover:text-[#988a7d] text-base block mb-0 transition-colors duration-200 md:flex md:items-center no-underline'
										>
											<span className='hidden md:inline-block mr-2'>
												{index + 1}.
											</span>
											<div dangerouslySetInnerHTML={sanitizeHTML(link.name)} />
										</Link>
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}


